import { Button, Choice, Destructive, Icon, Row, Slider, Switch, useToast } from '@efira/ui';

import { useOverlayStyle, useResetOverlayStyle, useSaveOverlayStyle } from '../../../api/queries';

/**
 * Панель показа легенды на оверлее - «HUD display settings» эталона.
 *
 * ПОДМЕНЯЕТ СПИСОК ПРИВЯЗОК, а не открывается рядом: в колонке шириной 300 px
 * две панели одновременно не помещаются, и у эталона это решено так же -
 * возврат кнопкой «Назад к привязкам».
 *
 * ЧТО ЭТО ВООБЩЕ ТАКОЕ. Легенда - строки «Роза → Толчок», которые зритель
 * видит в кадре постоянно. Это лучшее решение каталога эталона: обучающего
 * экрана не нужно. Здесь стример решает, где она стоит, сколько строк видно и
 * что в строке написано.
 *
 * ВСЕ ЧЕТЫРНАДЦАТЬ ОРГАНОВ УПРАВЛЕНИЯ ДЕЙСТВУЮТ НА ЖИВОЙ КАДР: сохранение
 * переиздаёт контракт, и оверлей перерисовывает легенду без перезагрузки.
 */
const POSITIONS = [
  ['sides', 'По краям'],
  ['top', 'Сверху'],
  ['bottom', 'Снизу'],
] as const;

const SIZES = [
  ['s', 'S'],
  ['m', 'M'],
  ['l', 'L'],
] as const;

const STYLES = [
  ['clean', 'Плашка', 'Строки на тёмной подложке: читаются на любом фоне'],
  ['outline', 'Контур', 'Без подложки, только рамка: меньше закрывает оверлей'],
] as const;

const ANIMATIONS = [
  ['off', 'Нет'],
  ['pulse', 'Пульс'],
  ['bounce', 'Прыжок'],
  ['shake', 'Тряска'],
  ['sway', 'Качание'],
] as const;

export function TriggerHud({ gameId, onBack }: { gameId: string; onBack: () => void }) {
  const style = useOverlayStyle(gameId);
  const save = useSaveOverlayStyle(gameId);
  const reset = useResetOverlayStyle(gameId);
  const toast = useToast();

  if (style.data === undefined) {
    return (
      <p className="text-foreground/80 text-sm">
        {style.isError ? 'Настройки показа не загрузились' : 'Настройки показа загружаются…'}
      </p>
    );
  }

  const values = style.data.values;
  const limits = style.data.limits;
  const enabled = values['enabled'] === true;

  const set = (id: string, value: boolean | number | string) => {
    save.mutate(
      { id, value },
      {
        onError: (error) => {
          const reason =
            typeof error === 'object' && error !== null && 'reason' in error
              ? String((error as { reason: unknown }).reason)
              : 'Настройка не применилась. Попробуйте ещё раз.';
          toast.show({ tone: 'warning', title: 'Не применилось', text: reason });
        },
      },
    );
  };

  /** Причина недоступности: выключенные подсказки не настраиваются. */
  const off = enabled ? undefined : 'Сначала включите подсказки';

  return (
    <div data-at="hud-panel" className="flex flex-col gap-3">
      <div data-control-row="at-hud-head" className="flex items-center gap-2 overflow-hidden">
        <Button variant="ghost" size="s" className="gap-1" data-at="back-btn" onClick={onBack}>
          <Icon name="chevronDown" size="sm" className="rotate-90" />
          <span className="@max-[240px]:hidden">Назад к привязкам</span>
        </Button>
        {/* Уточнённая область в подписи: рядом в колонке есть «Сбросить»
            всей игры, и одинаковые слова разного охвата путались. */}
        <Destructive
          id="hud-reset"
          className="ms-auto"
          data={{ 'data-at-hud': 'reset' }}
          icon={<Icon name="arrowReset" size="sm" />}
          label="Сбросить показ"
          title="Сбросить показ подсказок?"
          confirm="Сбросить"
          onConfirm={() => {
            reset.mutate(undefined, {
              onSuccess: () => {
                toast.show({ tone: 'success', title: 'Показ подсказок — заводской' });
              },
            });
          }}
        >
          Ваши настройки показа подсказок на оверлее сменятся заводскими. Привязки не тронутся.
        </Destructive>
      </div>

      {/* Три яруса, как у эталона: группа - блок - поле. Без подзаголовка
          группы непонятно, что всё ниже относится к подсказкам. */}
      <p className="text-foreground/80 text-xs font-semibold tracking-widest uppercase">Показ</p>

      <label className="flex items-center gap-2 text-sm font-semibold">
        <Switch
          data-at-hud="enable"
          checked={enabled}
          onCheckedChange={(next) => {
            set('enabled', next === true);
          }}
        />
        <span className="min-w-0 flex-1">Подсказки действий на экране</span>
        {/* Знак «?» с пояснением: без него «легенда» - слово из нашего
            словаря, а не из словаря стримера. */}
        <span
          data-at-hud="enable-help"
          tabIndex={0}
          role="note"
          aria-label="Строки «что чем запускается» на оверлее. Зритель видит их постоянно и не спрашивает в чате."
          title="Строки «что чем запускается» на оверлее. Зритель видит их постоянно и не спрашивает в чате."
          className="border-foreground/30 text-foreground/80 grid size-4 shrink-0 place-items-center rounded-full border text-xs"
        >
          ?
        </span>
      </label>

      <Segments
        id="position"
        title="Положение"
        options={POSITIONS}
        value={String(values['position'] ?? 'sides')}
        reason={off}
        onPick={(value) => {
          set('position', value);
        }}
      />

      <SliderField
        id="offset"
        title="Смещение по вертикали, px"
        field="offsetY"
        limits={limits['offsetY']}
        value={Number(values['offsetY'] ?? 0)}
        reason={off}
        onPick={(value) => {
          set('offsetY', value);
        }}
      />

      <Segments
        id="iconsize"
        title="Размер значков"
        options={SIZES}
        value={String(values['iconSize'] ?? 'm')}
        reason={off}
        onPick={(value) => {
          set('iconSize', value);
        }}
      />

      <SliderField
        id="perrow"
        title="Значков в ряду"
        field="perRow"
        limits={limits['perRow']}
        value={Number(values['perRow'] ?? 7)}
        reason={off}
        hint={
          values['position'] === 'sides'
            ? 'Подсказки выводятся двумя колонками: столько значков в каждой.'
            : undefined
        }
        onPick={(value) => {
          set('perRow', value);
        }}
      />

      <SliderField
        id="maxshown"
        title="Показывать не больше"
        field="maxShown"
        limits={limits['maxShown']}
        value={Number(values['maxShown'] ?? 14)}
        reason={off}
        hint="Остальные привязки срабатывают, но на оверлее не видны."
        onPick={(value) => {
          set('maxShown', value);
        }}
      />

      <fieldset className="flex flex-col gap-2" disabled={!enabled}>
        <legend className="text-foreground/80 mb-1 text-xs">Что писать в строке</legend>
        {(
          [
            ['showLabels', 'show-labels', 'Название действия'],
            ['showMultiplier', 'show-multiplier', 'Множитель'],
            ['showCount', 'show-count', 'Цену и порог'],
          ] as const
        ).map(([field, mark, title]) => (
          <label key={field} className="flex items-center gap-2 text-sm">
            <Switch
              data-at-hud={mark}
              checked={values[field] === true}
              onCheckedChange={(next) => {
                set(field, next === true);
              }}
            />
            {title}
          </label>
        ))}
      </fieldset>

      <fieldset className="flex flex-col gap-2" disabled={!enabled}>
        <legend className="text-foreground/80 mb-1 text-xs">Вид</legend>
        {/*
          НАБОР ВАРИАНТОВ С ПОЯСНЕНИЯМИ - КАРТОЧКАМИ, А НЕ СЕГМЕНТАМИ.
          У каждого вида есть подпись и пояснение под ней; сегментированный
          `Choice` их не вмещает. Роль при этом объявлена честно -
          радиогруппа, - и ворота «в наборе вариантов видно, что выбрано»
          проверяют её наравне с сегментами.
        */}
        <div role="radiogroup" aria-label="Вид легенды" className="grid grid-cols-2 gap-2">
          {STYLES.map(([value, title, hint]) => (
            /*
              КАРТОЧКА ВЫБОРА - РОЛЬ «СТРОКА СПИСКА» (`Row`), а не кнопка.
              Подпись и пояснение - две строки; у кнопки пресета высота
              фиксированная, и пояснение упиралось в её край. Выбранное
              показывает вариант строки, а не своя заливка (аудит единицы
              16, находка 20 и §4.4 инвентаризации пресета).
            */
            <Row
              key={value}
              asChild
              size="sm"
              variant={values['style'] === value ? 'outline' : 'default'}
              title={title}
              description={hint}
            >
              {/* eslint-disable-next-line no-restricted-syntax -- `asChild`
                  строки требует голого элемента; содержимое вкладывает Row. */}
              <button
                type="button"
                role="radio"
                data-at-hud="style"
                data-at-hud-value={value}
                aria-checked={values['style'] === value}
                onClick={() => {
                  set('style', value);
                }}
              />
            </Row>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-2" disabled={!enabled}>
        <legend className="text-foreground/80 mb-1 text-xs">Подарки в подсказках</legend>
        <label className="flex items-center gap-2 text-sm">
          <Switch
            data-at-hud="gifts-grey"
            checked={values['greyGifts'] === true}
            onCheckedChange={(next) => {
              set('greyGifts', next === true);
            }}
          />
          Серые значки вместо цветных
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Switch
            data-at-hud="gifts-only"
            checked={values['giftImagesOnly'] === true}
            onCheckedChange={(next) => {
              set('giftImagesOnly', next === true);
            }}
          />
          Только картинки, без названия подарка
        </label>
      </fieldset>

      <label className="flex flex-col gap-1 text-sm">
        <span className="flex items-center gap-2">
          <Switch
            data-at-hud="autohide"
            disabled={!enabled}
            checked={values['autoHide'] === true}
            onCheckedChange={(next) => {
              set('autoHide', next === true);
            }}
          />
          Ненадолго пропадать
        </span>
        <span className="text-foreground/80 text-xs">
          Раз в двадцать секунд подсказки уходят на секунду и открывают оверлей целиком.
        </span>
      </label>

      <Segments
        id="animation"
        title="Анимация значков"
        options={ANIMATIONS}
        value={String(values['animation'] ?? 'off')}
        reason={off}
        onPick={(value) => {
          set('animation', value);
        }}
      />
    </div>
  );
}

/** Сегменты: выбор одного из трёх-пяти, как у эталона. */
function Segments({
  id,
  title,
  options,
  value,
  reason,
  onPick,
}: {
  id: string;
  title: string;
  options: readonly (readonly [string, string])[];
  value: string;
  reason: string | undefined;
  onPick: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-foreground text-sm">{title}</span>
      <Choice
        label={title}
        value={value}
        onChange={onPick}
        size="sm"
        data-control-row={`at-hud-${id}`}
        options={options.map(([option, text]) => ({
          value: option,
          label: text,
          disabled: reason !== undefined,
          title: reason,
          data: { 'data-at-hud': id, 'data-at-hud-value': option },
        }))}
      />
      {reason !== undefined && <span className="text-foreground/80 text-xs">{reason}</span>}
    </div>
  );
}

/**
 * Ползунок с числом рядом.
 *
 * ЧИСЛО ВИДНО ВСЕГДА, а не только при перетаскивании: у эталона оно тоже
 * стоит справа, и без него «смещение −48» не отличить от «−52». Границы
 * названы числами - правило видимости 3.
 */
function SliderField({
  id,
  title,
  field,
  limits,
  value,
  reason,
  hint,
  onPick,
}: {
  id: string;
  title: string;
  field: string;
  limits: { min: number; max: number; step: number } | undefined;
  value: number;
  reason: string | undefined;
  hint?: string;
  onPick: (value: number) => void;
}) {
  const min = limits?.min ?? 0;
  const max = limits?.max ?? 100;
  const step = limits?.step ?? 1;

  return (
    <label className="flex flex-col gap-1">
      <span className="text-foreground text-sm">{title}</span>
      <span className="flex items-center gap-2">
        <Slider
          className="min-w-0 flex-1"
          data-at-hud={id}
          data-at-hud-field={field}
          min={min}
          max={max}
          step={step}
          value={[value]}
          disabled={reason !== undefined}
          title={reason}
          onValueChange={(next) => {
            onPick(next[0] ?? min);
          }}
        />
        <output className="text-foreground/80 w-12 shrink-0 text-end text-xs tabular-nums">
          {value}
        </output>
      </span>
      {/* Одно пояснение на поле, а не два подряд: три мелких серых абзаца
          подряд читаются как сплошная полоса. Знак минуса один и тот же в
          значении и в пояснении. */}
      <span className="text-foreground/80 text-xs">
        {hint === undefined ? `от ${min} до ${max}` : `${hint} От ${min} до ${max}.`}
      </span>
    </label>
  );
}
